import { Alert, Box } from '@mui/material';
import { getKioskPinStateRequest, setKioskPinRequest } from 'api/requests/profile-requests';
import UPButton from 'components/button';
import UPInput from 'components/input';
import { CloseAlertIcon, SectionContainer } from 'modules/companies/styles';
import { useEffect, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAppDispatch } from 'store';
import { setMenuStatusAction } from 'store/actions/menu-actions';
import {
  GENERAL__CANCEL_BUTTON,
  GENERAL__ERROR_MESSAGE,
  GENERAL__SAVE_BUTTON,
  PROFILE__PINCODE_ADD,
  PROFILE__PINCODE_CHANGE,
  PROFILE__PINCODE_CHOOSE,
  PROFILE__PINCODE_CONFLICT,
  PROFILE__PINCODE_HINT,
  PROFILE__PINCODE_LABEL,
  PROFILE__PINCODE_REPEAT,
  PROFILE__PINCODE_SAVED,
} from 'translations/constants';
import IKioskPinState from 'types/profile/IKioskPinState';
import { mapKioskError } from 'utils/kiosk-error';
import { ReactComponent as CloseIcon } from '../../assets/icons/Close.svg';
import { isPincodeValid } from './pincode-rules';
import PincodeMatchText from './PincodeMatchText';
import PincodeValidText from './PincodeValidText';
import { LeftContainer, PincodeChooseHeading } from './styles';

interface ISetPincodeForm {
  pincode: string;
  repeatPincode: string;
}

const sanitizeDigits = (value: string): string => value.replace(/\D/g, '').slice(0, 5);

const SUCCESS_NAVIGATE_DELAY_MS = 1200;

const SetPincode = () => {
  const [t] = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useAppDispatch();

  const routerState = location.state as IKioskPinState | null;
  const [pinState, setPinState] = useState<IKioskPinState | null>(routerState ?? null);
  const [submitting, setSubmitting] = useState(false);
  const [banner, setBanner] = useState<'success' | 'conflict' | 'error' | null>(null);
  const [errorMessage, setErrorMessage] = useState<string>(GENERAL__ERROR_MESSAGE);

  const {
    control,
    handleSubmit,
    watch,
    reset,
    formState: { errors },
  } = useForm<ISetPincodeForm>({ defaultValues: { pincode: '', repeatPincode: '' }, mode: 'onChange' });

  const pincode = watch('pincode');
  const repeatPincode = watch('repeatPincode');
  const pincodeValid = isPincodeValid(pincode);
  const pincodesMatch = repeatPincode.length > 0 && pincode === repeatPincode;

  useEffect(() => {
    (async () => {
      try {
        const fresh = await getKioskPinStateRequest();
        setPinState(fresh);
      } catch {
        // Keep whatever we had (router state or null) — a failed background refresh shouldn't block editing.
      }
    })();
  }, []);

  useEffect(() => {
    dispatch(setMenuStatusAction(t(pinState?.hasPin ? PROFILE__PINCODE_CHANGE : PROFILE__PINCODE_ADD)));
  }, [dispatch, t, pinState?.hasPin]);

  const onSubmit = async () => {
    if (!pincodeValid || !pincodesMatch) return;
    setSubmitting(true);
    setBanner(null);
    try {
      const result = await setKioskPinRequest(pincode, pinState?.rowVersion ?? null);
      setPinState(result);
      setBanner('success');
      window.setTimeout(() => navigate('/profile'), SUCCESS_NAVIGATE_DELAY_MS);
    } catch (e) {
      const httpStatus = (e as { response?: { status?: number } })?.response?.status;
      if (httpStatus === 409) {
        setBanner('conflict');
        try {
          const fresh = await getKioskPinStateRequest();
          setPinState(fresh);
        } catch {
          // Keep the stale state; the next submit attempt will simply 409 again.
        }
      } else {
        setErrorMessage(httpStatus === 400 ? mapKioskError(e) : GENERAL__ERROR_MESSAGE);
        setBanner('error');
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <SectionContainer>
      <LeftContainer>
        <PincodeChooseHeading>{t(PROFILE__PINCODE_CHOOSE)}</PincodeChooseHeading>

        <Alert severity="info" sx={{ marginBottom: '16px' }}>
          {t(PROFILE__PINCODE_HINT)}
        </Alert>

        <Box sx={{ width: '100%' }}>
          <Controller
            name="pincode"
            control={control}
            rules={{ required: true }}
            render={({ field }) => (
              <UPInput
                value={field.value}
                label={t(PROFILE__PINCODE_LABEL)}
                placeholder={t(PROFILE__PINCODE_LABEL)}
                type="password"
                maxLength={5}
                required
                error={!!errors.pincode}
                onChange={event => field.onChange(sanitizeDigits(event.target.value))}
              />
            )}
          />
          <Box width="100%" height={20}>
            {pincode.length > 0 && <PincodeValidText valid={pincodeValid} />}
          </Box>
        </Box>

        <Box mt={3} mb={3} sx={{ width: '100%' }}>
          <Controller
            name="repeatPincode"
            control={control}
            rules={{ required: true }}
            render={({ field }) => (
              <UPInput
                value={field.value}
                label={t(PROFILE__PINCODE_REPEAT)}
                placeholder={t(PROFILE__PINCODE_REPEAT)}
                type="password"
                maxLength={5}
                required
                error={!!errors.repeatPincode}
                onChange={event => field.onChange(sanitizeDigits(event.target.value))}
              />
            )}
          />
          <Box width="100%" height={20}>
            {repeatPincode.length > 0 && <PincodeMatchText match={pincodesMatch} />}
          </Box>
        </Box>

        <Box display="flex" gap={2}>
          <UPButton
            text={t(GENERAL__SAVE_BUTTON)}
            disabled={submitting || !pincodeValid || !pincodesMatch}
            onClick={handleSubmit(onSubmit)}
          />
          <UPButton
            text={t(GENERAL__CANCEL_BUTTON)}
            outlined
            onClick={() => {
              reset({ pincode: '', repeatPincode: '' });
              navigate('/profile');
            }}
          />
        </Box>

        {banner === 'success' && (
          <Alert
            severity="success"
            icon={false}
            variant="filled"
            sx={{ width: '100%', marginTop: '16px' }}
            action={
              <CloseAlertIcon onClick={() => setBanner(null)}>
                <CloseIcon fill="white" />
              </CloseAlertIcon>
            }
          >
            {t(PROFILE__PINCODE_SAVED)}
          </Alert>
        )}
        {banner === 'conflict' && (
          <Alert
            severity="error"
            icon={false}
            variant="filled"
            sx={{ width: '100%', marginTop: '16px' }}
            action={
              <CloseAlertIcon onClick={() => setBanner(null)}>
                <CloseIcon fill="white" />
              </CloseAlertIcon>
            }
          >
            {t(PROFILE__PINCODE_CONFLICT)}
          </Alert>
        )}
        {banner === 'error' && (
          <Alert
            severity="error"
            icon={false}
            variant="filled"
            sx={{ width: '100%', marginTop: '16px' }}
            action={
              <CloseAlertIcon onClick={() => setBanner(null)}>
                <CloseIcon fill="white" />
              </CloseAlertIcon>
            }
          >
            {t(errorMessage)}
          </Alert>
        )}
      </LeftContainer>
    </SectionContainer>
  );
};

export default SetPincode;
